<script setup lang="ts">
import { computed, ref } from 'vue';
import { RouterView } from 'vue-router';

import { UErrorView, useError } from '@hive/core';

import { useAuth } from '@/shared/composables/useAuth';

import type { NavigationMenuItem } from '@nuxt/ui';

const { error } = useError();
const hasError = computed(() => Boolean(error.value?.statusCode));
const isCriticalError = computed(() => error.value?.critical === true);

const { currentUser, isGranted, logout } = useAuth();

const open = ref(false);

const navigationItems = computed<NavigationMenuItem[]>(() => {
  const items: NavigationMenuItem[] = [];

  if (isGranted('hv:core:projects:list')) {
    items.push({
      label: 'Projects',
      icon: 'i-lucide-folder-kanban',
      to: '/projects',
      onSelect: () => {
        open.value = false;
      },
    });
  }

  return items;
});

const navigationUi = {
  root: 'gap-1',
  list: 'flex flex-col gap-1.5',
  link: 'gap-2.5 text-sm rounded-md px-2 py-1 before:inset-y-0 before:inset-x-0',
  linkLeadingIcon: 'shrink-0 size-4',
  linkLabel: 'truncate',
};

const userMenuItems = computed(() => [[{
  label: 'My profile',
  icon: 'i-lucide-user',
  to: '/me/edit',
}], [{
  label: 'Sign out',
  icon: 'i-lucide-log-out',
  onSelect: () => logout(),
}]]);
</script>

<template>
  <!-- Critical error: hide entire layout, show only error -->
  <template v-if="hasError && isCriticalError">
    <UErrorView />
  </template>

  <!-- Normal layout -->
  <template v-else>
    <UDashboardSidebar
      id="main"
      v-model:open="open"
      collapsible
      resizable
      :default-size="12"
      :min-size="10"
      :max-size="20"
      class="bg-muted"
      :ui="{ header: 'h-12 px-2', body: 'px-2 py-2' }"
    >
      <template #header="{ collapsed }">
        <UDropdownMenu
          :items="userMenuItems"
          :ui="{ content: 'min-w-56' }"
        >
          <UButton
            color="neutral"
            variant="ghost"
            class="w-full"
            data-testid="sidebar-user-button"
            :ui="{ base: 'gap-2 px-2 py-1', trailingIcon: 'ms-auto size-3.5 text-dimmed' }"
            :trailing-icon="collapsed ? undefined : 'i-lucide-chevron-down'"
          >
            <UAvatar
              :src="currentUser.avatar?.renditions?.[0]?.url"
              :alt="currentUser.fullName"
              size="2xs"
            />
            <span
              v-if="!collapsed"
              class="truncate text-sm font-medium text-highlighted"
              data-testid="sidebar-user-name"
            >
              {{ currentUser.fullName }}
            </span>
          </UButton>
        </UDropdownMenu>
      </template>

      <template #default="{ collapsed }">
        <UNavigationMenu
          :collapsed="collapsed"
          :items="navigationItems"
          :ui="navigationUi"
          orientation="vertical"
          tooltip
        />
      </template>
    </UDashboardSidebar>

    <div class="w-full min-h-svh flex flex-col">
      <!-- Non-critical error: show within layout -->
      <UErrorView v-if="hasError" />
      <RouterView v-else />
    </div>
  </template>
</template>
